import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { readConsent, writeConsent } from "@/lib/consent";

/**
 * GDPR / ePrivacy compliant cookie consent banner + granular settings modal.
 * Non-essential cookies remain OFF until the user explicitly opts in
 * (no pre-ticked boxes, aligned with CJEU C-673/17 "Planet49").
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [choices, setChoices] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    // Show banner only if no valid consent record exists
    const existing = readConsent();
    if (!existing) setVisible(true);
    else setChoices({ analytics: existing.analytics, marketing: existing.marketing });

    const reopen = () => setVisible(true);
    window.addEventListener("govans:open-consent", reopen);
    return () => window.removeEventListener("govans:open-consent", reopen);
  }, []);

  const acceptAll = () => {
    writeConsent({ analytics: true, marketing: true });
    setChoices({ analytics: true, marketing: true });
    setVisible(false);
    setShowSettings(false);
  };

  const rejectAll = () => {
    writeConsent({ analytics: false, marketing: false });
    setChoices({ analytics: false, marketing: false });
    setVisible(false);
    setShowSettings(false);
  };

  const saveChoices = () => {
    writeConsent(choices);
    setVisible(false);
    setShowSettings(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* ---- Banner (bottom-fixed) ---- */}
      {!showSettings && (
        <div
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
          data-testid="cookie-banner"
          className="fixed inset-x-3 bottom-3 sm:inset-x-6 sm:bottom-6 md:left-6 md:right-auto md:bottom-6 md:max-w-md z-[70] bg-white border border-black/10 shadow-2xl rounded-sm"
        >
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 shrink-0 bg-[#0055FF]/10 rounded-sm flex items-center justify-center">
                <Cookie className="w-5 h-5 text-[#0055FF]" strokeWidth={1.8} aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-sm font-extrabold text-[#0A0A0A] mb-1">We value your privacy</h2>
                <p className="text-xs text-[#475569] leading-relaxed">
                  GoVans.ie uses essential cookies to run the site. With your consent we also
                  use analytics and marketing cookies to improve the service and measure ads.
                  You can change your choice any time via the footer.{" "}
                  <Link to="/cookies" className="text-[#0055FF] font-bold underline underline-offset-2">Cookie Policy</Link>.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <button
                data-testid="cookie-accept-all"
                onClick={acceptAll}
                className="flex-1 h-11 px-4 bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-bold rounded-sm transition-colors"
              >
                Accept all
              </button>
              <button
                data-testid="cookie-reject-all"
                onClick={rejectAll}
                className="flex-1 h-11 px-4 bg-[#F1F5F9] hover:bg-[#E2E8F0] text-[#0A0A0A] text-sm font-bold rounded-sm transition-colors"
              >
                Reject non-essential
              </button>
            </div>
            <button
              data-testid="cookie-manage"
              onClick={() => setShowSettings(true)}
              className="mt-3 text-xs text-[#0055FF] font-bold underline underline-offset-2 hover:text-[#0044CC]"
            >
              Manage preferences
            </button>
          </div>
        </div>
      )}

      {/* ---- Settings modal ---- */}
      {showSettings && (
        <div
          className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setShowSettings(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Cookie preferences"
            data-testid="cookie-settings"
            className="bg-white rounded-sm max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-black/5 flex items-center justify-between">
              <h2 className="text-lg font-extrabold text-[#0A0A0A]">Cookie preferences</h2>
              <button
                aria-label="Close"
                data-testid="cookie-settings-close"
                onClick={() => setShowSettings(false)}
                className="w-11 h-11 flex items-center justify-center hover:bg-[#F1F5F9] rounded-sm"
              >
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <p className="text-sm text-[#475569] leading-relaxed">
                Choose which categories of cookies you allow. Essential cookies are always
                enabled because the site cannot function without them. See our{" "}
                <Link to="/cookies" className="text-[#0055FF] font-bold underline">
                  Cookie Policy
                </Link>{" "}
                for the full list of cookies used.
              </p>

              {/* Essential */}
              <div className="border border-black/10 rounded-sm p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-extrabold text-[#0A0A0A] mb-1">Strictly necessary</h3>
                    <p className="text-xs text-[#475569] leading-relaxed">
                      Required for the website to function (page navigation, form submission,
                      consent choice storage). Cannot be disabled.
                    </p>
                  </div>
                  <span className="text-xs font-bold text-[#0055FF] shrink-0 mt-1">Always on</span>
                </div>
              </div>

              {/* Analytics */}
              <div className="border border-black/10 rounded-sm p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-extrabold text-[#0A0A0A] mb-1">Analytics</h3>
                    <p className="text-xs text-[#475569] leading-relaxed">
                      PostHog session recording &amp; product analytics. Helps us understand which
                      pages are useful and where visitors get stuck. IP addresses are anonymised.
                    </p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      data-testid="cookie-toggle-analytics"
                      checked={choices.analytics}
                      onChange={(e) => setChoices({ ...choices, analytics: e.target.checked })}
                      className="sr-only peer"
                    />
                    <span className="w-11 h-6 bg-gray-300 peer-checked:bg-[#0055FF] rounded-full relative transition-colors">
                      <span className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
                    </span>
                  </label>
                </div>
              </div>

              {/* Marketing */}
              <div className="border border-black/10 rounded-sm p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-extrabold text-[#0A0A0A] mb-1">Marketing</h3>
                    <p className="text-xs text-[#475569] leading-relaxed">
                      Google Ads conversion tracking. Lets us measure whether our ads bring
                      customers to the site. No cross-site personal profiling.
                    </p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      data-testid="cookie-toggle-marketing"
                      checked={choices.marketing}
                      onChange={(e) => setChoices({ ...choices, marketing: e.target.checked })}
                      className="sr-only peer"
                    />
                    <span className="w-11 h-6 bg-gray-300 peer-checked:bg-[#0055FF] rounded-full relative transition-colors">
                      <span className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-black/5 flex flex-col sm:flex-row gap-2">
              <button
                data-testid="cookie-settings-save"
                onClick={saveChoices}
                className="flex-1 h-11 px-4 bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-bold rounded-sm transition-colors"
              >
                Save preferences
              </button>
              <button
                data-testid="cookie-settings-accept-all"
                onClick={acceptAll}
                className="flex-1 h-11 px-4 bg-[#F1F5F9] hover:bg-[#E2E8F0] text-[#0A0A0A] text-sm font-bold rounded-sm transition-colors"
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
